import type { ModelData } from '@/types/models';
import { formatCurrency, formatNumber } from '@/utils/formatters';

interface RevenueTabProps {
  data: ModelData;
}

export function RevenueTab({ data }: RevenueTabProps) {
  // Safely extract data with defaults
  const years = data?.years || [];
  const customers = data?.customers || [];
  const revenue = data?.revenue || [];

  // Handle case where data is not yet loaded
  if (!years.length || !revenue.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-4xl mb-3">📊</div>
          <p className="text-slate-500">No revenue data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-4 font-semibold text-slate-700 bg-slate-50">
              Metric
            </th>
            {years.map((year) => (
              <th
                key={year}
                className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-50"
              >
                Year {year}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {/* Customers */}
          {customers.length > 0 && (
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-900">
                Active Customers
              </td>
              {customers.map((count, i) => (
                <td key={i} className="py-3 px-4 text-right text-slate-700">
                  {formatNumber(count)}
                </td>
              ))}
            </tr>
          )}

          {/* Revenue Breakdown */}
          {revenue[0]?.subscription !== undefined && (
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700 pl-8">
                Subscription Revenue
              </td>
              {revenue.map((rev, i) => (
                <td key={i} className="py-3 px-4 text-right text-slate-600">
                  {formatCurrency(rev?.subscription || 0)}
                </td>
              ))}
            </tr>
          )}

          {revenue[0]?.usage !== undefined && (
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700 pl-8">
                Usage Revenue
              </td>
              {revenue.map((rev, i) => (
                <td key={i} className="py-3 px-4 text-right text-slate-600">
                  {formatCurrency(rev?.usage || 0)}
                </td>
              ))}
            </tr>
          )}

          {revenue[0]?.expansion !== undefined && (
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700 pl-8">
                Expansion Revenue
              </td>
              {revenue.map((rev, i) => (
                <td key={i} className="py-3 px-4 text-right text-slate-600">
                  {formatCurrency(rev?.expansion || 0)}
                </td>
              ))}
            </tr>
          )}

          {/* Product/Service Revenue (for non-SaaS models) */}
          {revenue[0]?.product !== undefined && (
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700 pl-8">
                Product Revenue
              </td>
              {revenue.map((rev, i) => (
                <td key={i} className="py-3 px-4 text-right text-slate-600">
                  {formatCurrency(rev?.product || 0)}
                </td>
              ))}
            </tr>
          )}

          {revenue[0]?.service !== undefined && (
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700 pl-8">
                Service Revenue
              </td>
              {revenue.map((rev, i) => (
                <td key={i} className="py-3 px-4 text-right text-slate-600">
                  {formatCurrency(rev?.service || 0)}
                </td>
              ))}
            </tr>
          )}

          {revenue[0]?.commission !== undefined && (
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700 pl-8">
                Commission Revenue
              </td>
              {revenue.map((rev, i) => (
                <td key={i} className="py-3 px-4 text-right text-slate-600">
                  {formatCurrency(rev?.commission || 0)}
                </td>
              ))}
            </tr>
          )}

          {/* Total Revenue */}
          <tr className="bg-primary-50 font-semibold">
            <td className="py-3 px-4 text-primary-900">Total Revenue</td>
            {revenue.map((rev, i) => (
              <td key={i} className="py-3 px-4 text-right text-primary-900">
                {formatCurrency(rev?.total || 0)}
              </td>
            ))}
          </tr>

          {/* Growth Rate */}
          <tr className="hover:bg-slate-50">
            <td className="py-3 px-4 font-medium text-slate-700">
              YoY Growth Rate
            </td>
            {revenue.map((rev, i) => {
              const prevRev = i > 0 ? revenue[i - 1]?.total : rev?.total;
              const currentRev = rev?.total || 0;
              const growth =
                i === 0 || !prevRev ? 0 : ((currentRev - prevRev) / prevRev) * 100;
              return (
                <td
                  key={i}
                  className={`py-3 px-4 text-right ${
                    growth > 0
                      ? 'text-green-600'
                      : growth < 0
                      ? 'text-red-600'
                      : 'text-slate-500'
                  }`}
                >
                  {i === 0 ? '-' : `${growth.toFixed(1)}%`}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
